import { uniq } from "lodash-es";
import { useMemo } from "react";

import type { ConnectedProjectDto } from "@/api/types";
import { useSuperTokensUserId } from "@/authentication/AuthenticationStateProvider";
import { isDefined } from "@/helpers/util";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useConfig } from "@/providers/config/useConfig";

export function useRecentProjects(
  projectId: string,
  showCurrentProject: boolean,
  recentProjectCount: 3,
): {
  recentProjects: ConnectedProjectDto[];
  isLoading: boolean;
  projects: ConnectedProjectDto[];
} {
  const { data: projects = [], isLoading } = useLinkedProjects();
  const envLongName = useConfig("envLongName");
  const userId = useSuperTokensUserId();

  const recentProjectsKey = envLongName && userId ? `${envLongName}.${userId}.recent-projects` : undefined;
  const localStorageProjectList =
    isLoading || !recentProjectsKey ? [] : getLocalStorageValue<string[]>(recentProjectsKey, []);

  const recentProjectIds = useMemo(() => {
    if (!recentProjectsKey) {
      return [];
    }

    if (!localStorageProjectList?.length) {
      const initialState = [projectId];
      updateLocalStorage(recentProjectsKey, initialState);

      return initialState;
    } else if (localStorageProjectList[0] !== projectId) {
      const cleanedLocalStorageProjectList = localStorageProjectList.filter((x) => projects.some((p) => p.id === x));
      const projectIds = uniq([projectId, ...cleanedLocalStorageProjectList]);
      updateLocalStorage(recentProjectsKey, projectIds);

      return projectIds;
    } else {
      return localStorageProjectList;
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [recentProjectsKey, projectId, localStorageProjectList, projects]);

  const recentProjects = useMemo(() => {
    const topRecentProjects = recentProjectIds
      .filter((x) => showCurrentProject || x !== projectId)
      .slice(0, recentProjectCount);

    return topRecentProjects.map((recentProjectId) => projects.find((p) => p.id === recentProjectId)).filter(isDefined);
  }, [projectId, projects, recentProjectIds, showCurrentProject, recentProjectCount]);

  return useMemo(() => ({ recentProjects, isLoading, projects }), [recentProjects, isLoading, projects]);
}
